import React from 'react'
import { Box, Typography } from '@mui/material'
import { WeatherIcon, WeatherCondition } from '../WeatherIcons'

// ---------------- Helpers (pure) ----------------
export function formatMaybe(n?: number, suffix?: string): string | undefined {
  const sfx = typeof suffix === 'string' ? suffix : ''
  return typeof n === 'number' ? n.toFixed(1) + sfx : undefined
}

export function formatFixed(
  n?: number,
  digits: number = 1,
): string | undefined {
  return typeof n === 'number' ? n.toFixed(digits) : undefined
}

// ---- Icon selector (exported for external use) ----
export interface WeatherReportData {
  rainFall?: number
  rainRateHigh?: number
  solarRadAvg?: number
  windSpeedHigh?: number
  humidity?: number
  temperatureOutsideAvg?: number
}

export function selectWeatherIcon(data?: WeatherReportData): WeatherCondition {
  if (!data) return WeatherCondition.normal
  const {
    rainFall,
    rainRateHigh,
    solarRadAvg,
    windSpeedHigh,
    humidity,
    temperatureOutsideAvg,
  } = data
  if (rainRateHigh && windSpeedHigh && rainRateHigh > 5 && windSpeedHigh > 20)
    return WeatherCondition.stormy
  if (
    rainFall &&
    temperatureOutsideAvg &&
    rainFall > 0 &&
    temperatureOutsideAvg <= 2
  )
    return WeatherCondition.snowy
  if ((rainFall && rainRateHigh && rainFall > 0) || (rainRateHigh ?? 0) > 1)
    return WeatherCondition.rainy
  if ((solarRadAvg ?? 0) < 150 && (humidity ?? 0) > 60)
    return WeatherCondition.cloudy
  if ((solarRadAvg ?? 0) >= 150 && (solarRadAvg ?? 0) < 300)
    return WeatherCondition.partlyCloudy
  if ((solarRadAvg ?? 0) >= 300 && (rainFall ?? 0) === 0)
    return WeatherCondition.sunny
  return WeatherCondition.normal
}

// ---------------- Types ----------------
export interface WeatherStation {
  id: string
  name: string
  temperatureC?: number
  humidityPct?: number
  windMs?: number
  condition?: WeatherCondition
  et24mm?: number
  precip24mm?: number
  rainRateMmHr?: number
  rain1hMm?: number
}

// ---------------- Liquid Glass Card Wrapper (MUI) ----------------
export function GlassCard(props: {
  title?: React.ReactNode
  right?: React.ReactNode
  children?: React.ReactNode
  sx?: any
}) {
  const { title, right, children, sx } = props
  // Normalize string titles to Typography with larger font
  const titleNode =
    typeof title === 'string' ? (
      <Typography noWrap sx={{ color: 'white', fontSize: 16, fontWeight: 700 }}>
        {title}
      </Typography>
    ) : (
      title
    )
  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        borderRadius: 3,
        bgcolor: 'rgba(15,23,42,0.38)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.25)',
        boxShadow: '0 10px 40px rgba(0,0,0,0.35)',
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      {(title || right) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            minHeight: 44,
            borderBottom: '1px solid rgba(255,255,255,0.15)',
          }}
        >
          <Box className="drag-handle" sx={{ minWidth: 0 }}>
            {titleNode}
          </Box>
          <Box className="nodrag">{right}</Box>
        </Box>
      )}

      {/* sheen overlays */}
      <Box
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          inset: 0,
          borderRadius: 3,
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
        }}
      />
      <Box
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          top: 8,
          left: 8,
          right: 8,
          height: 36,
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.10)',
          filter: 'blur(10px)',
        }}
      />

      {/* content */}
      <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>{children}</Box>
    </Box>
  )
}

// ---------------- Weather Station Card ----------------
export default function WeatherStationCard(props: { station: WeatherStation }) {
  const station = props.station
  const rootRef = React.useRef<HTMLDivElement | null>(null)
  const [scale, setScale] = React.useState(1)

  React.useEffect(() => {
    if (!rootRef.current) return
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect
      if (!cr) return
      // Base design size; tune as needed for your layout
      const baseW = 420
      const baseH = 620
      const sW = cr.width / baseW
      const sH = cr.height / baseH
      // Use the smaller of width/height so short heights also scale text down
      const sCombined = 0.5 * sW + 0.7 * sH
      const s = Math.max(0.7, Math.min(1.5, sCombined))
      setScale(Number.isFinite(s) ? s : 1)
    })
    ro.observe(rootRef.current)
    return () => ro.disconnect()
  }, [])

  // Helper for responsive font sizing via CSS var
  const font = (px: number) => `calc(var(--ws-scale, 1) * ${px}px)`
  const iconSize = Math.max(32, Math.round(64 * scale))

  return (
    <GlassCard
      title={
        <Typography
          noWrap
          sx={{ color: 'white', fontSize: 16, fontWeight: 700 }}
        >
          {station.name}
        </Typography>
      }
    >
      <Box
        ref={rootRef}
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          ['--ws-scale' as any]: scale,
        }}
      >
        <Box
          sx={{
            p: 1,
            height: '75%',
            display: 'grid',
            gridTemplateColumns: '50% 50%',
            columnGap: 1,
            alignItems: 'stretch',
          }}
        >
          {/* Left: Condition icon + big temperature + label */}
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              gap: 1,
            }}
          >
            {/* subtle circular glow behind icon */}
            <Box
              sx={{
                position: 'absolute',
                top: 4,
                left: 2,
                width: 72,
                height: 72,
                borderRadius: '50%',
                background:
                  'radial-gradient(closest-side, rgba(255,255,255,0.25), transparent 70%)',
                filter: 'blur(2px)',
              }}
            />
            <Box sx={{ position: 'relative' }}>
              <WeatherIcon
                condition={station.condition ?? WeatherCondition.normal}
                width={iconSize}
                height={iconSize}
              />
            </Box>
            <Box
              sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1 }}
            >
              <Typography
                sx={{
                  fontSize: font(36),
                  fontWeight: 700,
                  color: 'white',
                  lineHeight: 1,
                }}
              >
                {formatFixed(station.temperatureC, 1) || '—'}
              </Typography>
              <Typography
                sx={{ fontSize: font(14), fontWeight: 600, color: 'white' }}
              >
                °C
              </Typography>
            </Box>
            <Typography
              sx={{
                color: 'white',
                opacity: 0.95,
                letterSpacing: 0.8,
                fontSize: font(11),
              }}
            >
              TEMPERATURE
            </Typography>
          </Box>

          {/* Right: three stacked metrics with separators */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateRows: '1fr auto 1fr auto 1fr',
              alignItems: 'center',
            }}
          >
            {/* ET 24 Hours */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: font(24),
                    fontWeight: 700,
                    color: 'white',
                    lineHeight: 1,
                  }}
                >
                  {formatFixed(station.et24mm, 1) || '—'}
                </Typography>
                <Typography sx={{ fontSize: font(14), color: 'white' }}>
                  mm
                </Typography>
              </Box>
              <Typography sx={{ color: 'white', fontSize: font(11) }}>
                ET 24 Hours
              </Typography>
            </Box>
            <Box
              sx={{
                borderTop: '1px solid rgba(255,255,255,0.35)',
                my: 0.5,
                width: '70%',
              }}
            />

            {/* Humidity */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: font(24),
                    fontWeight: 700,
                    color: 'white',
                    lineHeight: 1,
                  }}
                >
                  {formatFixed(station.humidityPct, 0) || '—'}
                </Typography>
                <Typography sx={{ fontSize: font(14), color: 'white' }}>
                  %
                </Typography>
              </Box>
              <Typography sx={{ color: 'white', fontSize: font(11) }}>
                Humidity
              </Typography>
            </Box>
            <Box
              sx={{
                borderTop: '1px solid rgba(255,255,255,0.35)',
                my: 0.5,
                width: '70%',
              }}
            />

            {/* Wind Speed (use windMs) */}
            <Box>
              <>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: font(24),
                      fontWeight: 700,
                      color: 'white',
                      lineHeight: 1,
                    }}
                  >
                    {formatFixed(station.windMs, 1) || '—'}
                  </Typography>
                  <Typography sx={{ fontSize: font(14), color: 'white' }}>
                    m/s
                  </Typography>
                </Box>
                <Typography sx={{ color: 'white', fontSize: font(11) }}>
                  Wind Speed
                </Typography>
              </>
            </Box>
          </Box>
        </Box>

        {/* Bottom bar: three small stats */}
        <Box sx={{ px: 2, pb: 2 }}>
          <Box
            sx={{
              borderTop: '1px solid rgba(255,255,255,0.35)',
              mt: 0.75,
              pt: 0.75,
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: 'white',
                  fontSize: font(14),
                  lineHeight: 1.2,
                }}
              >
                {(typeof station.rainRateMmHr === 'number'
                  ? formatFixed(station.rainRateMmHr, 1)
                  : '-') + ' mm/h'}
              </Typography>
              <Typography sx={{ color: 'white', fontSize: font(11) }}>
                Rain Rate
              </Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: 'white',
                  fontSize: font(14),
                  lineHeight: 1.2,
                }}
              >
                {(typeof station.rain1hMm === 'number'
                  ? formatFixed(station.rain1hMm, 1)
                  : '-') + ' mm'}
              </Typography>
              <Typography sx={{ color: 'white', fontSize: font(11) }}>
                Rain 1 Hour
              </Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: 'white',
                  fontSize: font(14),
                  lineHeight: 1.2,
                }}
              >
                {(typeof station.precip24mm === 'number'
                  ? formatFixed(station.precip24mm, 1)
                  : '-') + ' mm'}
              </Typography>
              <Typography sx={{ color: 'white', fontSize: font(11) }}>
                Rain 24 Hours
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </GlassCard>
  )
}
